import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks";
import { useGetAttachedDocumentsQuery } from "../api";
import { PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "../consts";
import type { AttachedDocumentListItem } from "../types";

export type SortBy =
  | "date_desc"
  | "date_asc"
  | "name_asc"
  | "name_desc"
  | "size_desc"
  | "size_asc";

export const SORT_OPTIONS: SortBy[] = [
  "date_desc",
  "date_asc",
  "name_asc",
  "name_desc",
  "size_desc",
  "size_asc",
];

export function useAttachedDocumentsListPage() {
  const { t } = useTranslation("AttachedDocumentsPage");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  useEffect(function resetPageOnSearch() {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching, isError } = useGetAttachedDocumentsQuery({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
  });

  const rawDocuments = useMemo(function getRawDocuments() {
    return data?.results ?? [];
  }, [data]);

  const filteredDocuments = useMemo(function applyClientFilters() {
    let result = rawDocuments;

    if (typeFilter) {
      result = result.filter((doc) => doc.content_type === typeFilter);
    }

    if (dateRange.start || dateRange.end) {
      result = result.filter(function filterByDate(doc) {
        const uploadDate = new Date(doc.uploaded_at);
        uploadDate.setHours(0, 0, 0, 0);
        if (dateRange.start) {
          const start = new Date(dateRange.start);
          start.setHours(0, 0, 0, 0);
          if (uploadDate < start) return false;
        }
        if (dateRange.end) {
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999);
          if (uploadDate > end) return false;
        }
        return true;
      });
    }

    return result;
  }, [rawDocuments, typeFilter, dateRange]);

  const documents = useMemo(function applySorting() {
    return [...filteredDocuments].sort(function compareDocs(a: AttachedDocumentListItem, b: AttachedDocumentListItem) {
      switch (sortBy) {
        case "date_desc":
          return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
        case "date_asc":
          return new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "size_desc":
          return b.file_size - a.file_size;
        case "size_asc":
          return a.file_size - b.file_size;
      }
    });
  }, [filteredDocuments, sortBy]);

  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const fromItem = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toItem = Math.min(totalCount, page * PAGE_SIZE);

  useEffect(function handleEmptyPage() {
    if (!isLoading && documents.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [isLoading, documents.length, page]);

  const hasActiveSearch = !!search.trim();
  const hasActiveFilters = hasActiveSearch || !!typeFilter || !!dateRange.start || !!dateRange.end;

  function handleResetFilters() {
    setSearch("");
    setTypeFilter(null);
    setDateRange({ start: null, end: null });
  }

  return {
    documents,
    isLoading,
    isFetching,
    isError,
    search,
    setSearch,
    page,
    setPage,
    totalPages,
    totalCount,
    fromItem,
    toItem,
    hasActiveSearch,
    hasActiveFilters,
    typeFilter,
    setTypeFilter,
    dateRange,
    setDateRange,
    sortBy,
    setSortBy,
    handleResetFilters,
    t,
  };
}
